import {
    request,
    requestWithMsg,
    downloadFile,
    uploadFile,
    MsgResult,
    PageResult,
    fetchFileBlob,
} from './request';
import {
    DeleteCascadePayload,
    fetchDeletePreview,
    RelatedGroup,
} from './cascade';
import { I18n, i18nText } from '../utils/i18n';

export interface Material {
    id: number;
    uuid: string;
    xcode: string;
    legacyMid?: number;
    /** 多语言名称 map, 任意 locale key (zh/en 必填, 其余语种如 de/fr/ja/zh-hant 可选) */
    name: I18n;
    image?: string;
    type1?: string;
    type2?: string;
    craft1?: string;
    craft2?: string;
    surface1?: string;
    surface2?: string;
    color?: string;
    colorHex?: string;
    target?: string;
    coatingMm?: number;
    /** 厚度 mm (原料适用, 0=未知; 名称如 "18mm胶合板" 解析预填, 可手工修正) */
    thickness?: number;
    gloss?: string;
    traits?: string;
    /** 材料使用说明链接 (教程 URL); 空 = 用一级分类上配的默认链接 (见 MaterialDetail.usageDocUrlEffective) */
    usageDocUrl?: string;
    /** 使用说明展示标题 (与链接成对; 链接为空时本字段不生效, 标题跟着链接一起回退到一级分类) */
    usageDocTitle?: string;
    /** 贴图文件完整 URL (jpg/png/svg/webp, ≤50MB); 空 = 无贴图 */
    textureUrl?: string;
    /** 卷料 Y 轴传送比 (列名同 CMS, 最多 5 位小数); null = 未配置 */
    axisYTransmissionRatioRollMaterial?: number | null;
    /** 材料柔韧性: rigid(硬性) / flexible(软性) / ''(未填) */
    flexibility?: string;
    /** 材料特性 code 数组, 选项来自 dict material_trait */
    characteristics?: string[];
    status: string;
    /** 是否已发布 1 未发布 / 2 已发布 (2026-09-03; 常量见 components/PublishBadge): 只约束 OpenAPI 对外下发, 平台内不受限; 与 status 是两个维度 */
    isPublished: number;
    /** 最近一次发布时间 (unix 秒, 0 = 从未发布; 取消发布不清零) */
    publishedAt?: number;
    createdBy: number;
    updatedBy: number;
    remark?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface MaterialListItem extends Material {
    skuCount: number;
    /** 该材料启用的 cap_key 列表 (光源 × 工艺), 列表行 badge 直接用. */
    caps?: string[];
}

export interface MaterialOptical {
    xcode: string;
    bandNm: number;
    colorAbsorb?: number;
    materialAbsorb?: number;
    reflectance?: number;
    damageThreshold?: number;
}

export interface MaterialCapability {
    xcode: string;
    capKey: string;
    /** 推荐 (适配) —— 下游按它判能否加工; 不推荐与未设置都是 false */
    isEnabled: boolean;
    note?: string;
    /** 推荐状态 (2026-10-28): 1 推荐 / 2 不推荐; 与 isEnabled 合判, 见 capRecState */
    recStatus?: number;
    /** 推荐项排序号 (1 起, 越小越靠前); 0 = 未排过序, 后端已按 sno,id 排好 */
    sno?: number;
    /** 该材料在此工艺类型下被禁用的设备 deviceKey (2026-11-03); 缺省 / 空 = 全部不禁用 */
    disabledDevices?: string[];
}

/** 推荐状态码 (material_capability.rec_status). */
export const CAP_REC_NOT_RECOMMENDED = 2;

export type CapRecState = 'rec' | 'not' | 'none';

/** 推荐 = isEnabled; 不推荐 = 未启用且 recStatus=2; 其余 (含历史停用行) = 未设置. */
export function capRecState(c: MaterialCapability): CapRecState {
    if (c.isEnabled) return 'rec';
    return c.recStatus === CAP_REC_NOT_RECOMMENDED ? 'not' : 'none';
}

/** SKU 本体 (2026-08-24 多对多改造: 不再有 xcode 字段, 绑定关系见 SkuRow.materials). */
export interface MaterialSku {
    id: number;
    uuid: string;
    skuCode: string;
    /** 多语言商品名 map, 任意 locale key */
    productName?: I18n;
    /** 属性: 字典 sku_prop 的 code (raw 原材料 / semi_finished 半成品 …) / ''(未标); 展示用 components/SkuProp */
    prop: string;
    /** CMS 耗材ID (即原"关联ID", 两者合一); 0 = 待映射 */
    cmsId: number;
    qrAttachmentId?: number;
    status: string;
    /** 规格类型 (2026-10-14) → sku_schema.groupKey; '' = 未选. 原材料必须选并填齐必填项 */
    specGroup?: string;
    /** 规格值 JSON 字符串 {fieldKey: value}; 展示 / 编辑用 utils/skuSpec */
    specParams?: string;
    /** 形态 (2026-10-19): single 单品 / bundle SKU 集合 (由若干单品 × 数量组成, 见 SkuRow.items) */
    kind?: SkuKind;
    createdBy?: number;
    updatedBy?: number;
    remark?: string;
    createdAt?: string;
    updatedAt?: string;
}

/** SKU 形态: 功能枚举 (与后端 models.SkuKindSingle / SkuKindBundle 一致), 程序按它分支. */
export type SkuKind = 'single' | 'bundle';
export const SKU_KIND_SINGLE: SkuKind = 'single';
export const SKU_KIND_BUNDLE: SkuKind = 'bundle';

/** SKU 集合组成的一行 (提交用): 含 qty 个 skuCode (单品). */
export interface SkuItemInput {
    skuCode: string;
    qty: number;
}

/** 列表行里的集合组成: 带子 SKU 商品名; missing = 子 SKU 已不在库 */
export interface SkuItemRow extends SkuItemInput {
    productName?: I18n;
    missing?: boolean;
}

/** SKU 的一条材料绑定 (服务端按 主绑定在前 排序返回). */
export interface SkuBoundMaterial {
    xcode: string;
    name?: I18n;
    /** 主绑定: 每个已绑定 SKU 恰有一条 (扫码等场景的默认材料) */
    isPrimary: boolean;
    /** 该材料自身启用的 cap_key (2026-09-08): 列表按材料逐行渲染适配工艺 */
    caps?: string[];
}

/** SKU 库平铺行: SKU + 绑定材料清单 (各带自身工艺类型) + 工艺类型并集 (多对多, 2026-08-24). */
export interface SkuRow extends MaterialSku {
    /** 绑定的材料 (主绑定在前); 空数组 = 未绑定 */
    materials: SkuBoundMaterial[];
    /** 绑定材料启用的 cap_key 并集 (非 SKU 自身字段) */
    caps: string[];
    /** SKU 集合的组成 (kind=bundle 才有, 单品为 null) */
    items?: SkuItemRow[] | null;
}

/** SKU 行绑定的材料编码列表 (主绑定在前). */
export const skuBindingXcodes = (s: SkuRow): string[] =>
    (s.materials || []).map((m) => m.xcode);

/** 理化参数分组 (Schema 驱动): 字段由 Schema 定义, params 为该材质的值 (未录入为 null). */
export interface PhysGroupField {
    key: string;
    label: string;
    unit: string;
    /** 枚举选项: 非空时值只能从中选 (编辑处渲染下拉) */
    options?: string[];
    /** 值类型 (与设备字段组同口径): str / bool / array, 缺省数值 */
    type?: string;
    /** 值来源字典类目 code; 录入渲染字典下拉 */
    optionsSource?: string;
}

/** 单个理化字段值: 数值 / 文本 / 是否 (bool 字段) / 列表 (array 字段), 与设备字段组同口径 */
type PhysFieldValue = number | string | boolean | string[];

/** 组值: flat 布局 {key: value}; matrix 布局 {rowCode: {key: value}} */
export type PhysGroupParams = Record<
    string,
    PhysFieldValue | Record<string, PhysFieldValue>
>;

export interface PhysGroup {
    groupKey: string;
    label: string;
    /** 数据来源标注, 如 "xcs material_craft_configs" */
    src: string;
    /** 布局: flat=平铺(默认) / matrix=行(字典 rowSource)×列(fields) */
    layout?: string;
    rowSource?: string;
    /** 矩阵行维度标题 (表头第一列), 如 "波长 (nm)" */
    rowLabel?: string;
    fields: PhysGroupField[];
    params: PhysGroupParams | null;
}

export interface MaterialDetail extends Material {
    optical: MaterialOptical[];
    capability: MaterialCapability[];
    /** 理化参数分组 (Schema 驱动, 替代旧 physical 单表字段) */
    physGroups: PhysGroup[];
    skus: MaterialSku[];
    skuCount: number;
    relatedParamCount: number;
    /** 兜底解析后的使用说明链接: 材料自己配了取自己的, 否则取一级分类的默认链接 (后端算好, 展示直接用) */
    usageDocUrlEffective?: string;
    /** 兜底解析后的使用说明标题 (与链接成对回退, 空则展示端用兜底文案) */
    usageDocTitleEffective?: string;
}

export interface ListMaterialsParams {
    keyword?: string;
    type1?: string;
    type2?: string;
    status?: string;
    /** 发布状态筛选 1 未发布 / 2 已发布, 不传 = 全部 */
    isPublished?: number;
    /** 是否有贴图: true 有 / false 无, 不传 = 全部 */
    hasTexture?: boolean;
    page?: number;
    pageSize?: number;
}

export interface CreateMaterialPayload {
    xcode: string;
    legacyMid?: number;
    /** 多语言名称 map, 后端要求 zh/en 至少一个非空 */
    name: I18n;
    image?: string;
    type1?: string;
    type2?: string;
    craft1?: string;
    craft2?: string;
    surface1?: string;
    surface2?: string;
    color?: string;
    colorHex?: string;
    target?: string;
    coatingMm?: number;
    /** 厚度 mm (原料适用, 0=未知; 名称如 "18mm胶合板" 解析预填, 可手工修正) */
    thickness?: number;
    gloss?: string;
    traits?: string;
    /** 材料使用说明链接 (教程 URL); 空 = 回退一级分类的默认链接 */
    usageDocUrl?: string;
    /** 使用说明展示标题 (与链接成对) */
    usageDocTitle?: string;
    /** 贴图文件完整 URL; 编辑时传空串 = 删除贴图 */
    textureUrl?: string;
    /** 卷料传送比 (> 0, 最多 5 位小数); 编辑时传 0 = 清空 */
    axisYTransmissionRatioRollMaterial?: number;
    flexibility?: string;
    characteristics?: string[];
    status?: string;
    remark?: string;
}

export type UpdateMaterialPayload = Partial<
    Omit<CreateMaterialPayload, 'xcode'>
>;

const BASE = '/v1';

/** 后端列表接口的 pageSize 上限 (material handler maxPageSize). */
const MAX_PAGE_SIZE = 100;

export function listMaterials(
    params: ListMaterialsParams,
): Promise<PageResult<MaterialListItem>> {
    return request<PageResult<MaterialListItem>>(`${BASE}/materials`, {
        query: params,
    });
}

/**
 * SearchSelect 异步分页加载器: 服务端关键词搜索, 每页 50 条, 滚动到底翻页.
 * 材料 1300+, 下拉场景不做全量拉取.
 */
export async function loadMaterialOptions(
    query: string,
    page: number,
): Promise<{ list: { value: string; label: string }[]; hasMore: boolean }> {
    const pageSize = 50;
    const res = await listMaterials({
        keyword: query || undefined,
        page,
        pageSize,
    });
    return {
        list: (res.list || []).map((m) => ({
            value: m.xcode,
            label: `${m.xcode} · ${i18nText(m.name)}`,
        })),
        hasMore: page * pageSize < res.totalCount,
    };
}

/**
 * 拉取全部材料 (分页循环防后端 pageSize 上限).
 * 仅供确需完整枚举的场景 (如导出勾选弹窗的全选); 下拉请用 loadMaterialOptions.
 */
export async function listAllMaterials(): Promise<MaterialListItem[]> {
    // 后端 pageSize 上限 100 (maxPageSize): 1400+ 材料串行翻页要十几秒, 弹窗会一直"加载中".
    // 先拉第一页拿总数, 其余页并行 (2026-09-13).
    const first = await listMaterials({ page: 1, pageSize: MAX_PAGE_SIZE });
    const all = [...(first.list || [])];
    const pages = Math.ceil((first.totalCount || 0) / MAX_PAGE_SIZE);
    if (pages > 1) {
        const rest = await Promise.all(
            Array.from({ length: pages - 1 }, (_, i) =>
                listMaterials({ page: i + 2, pageSize: MAX_PAGE_SIZE }),
            ),
        );
        rest.forEach((r) => all.push(...(r.list || [])));
    }
    return all;
}

/**
 * 导出 xlsx: 材质编码 / SKU / 适配机型 / 对应加工类型.
 * xcodes 非空 → POST 只导勾选的材料; 不传 → GET 导全部.
 */
export function exportMaterials(xcodes?: string[]): Promise<void> {
    return downloadFile(
        `${BASE}/materials-export`,
        'materials-export.xlsx',
        xcodes && xcodes.length > 0 ? { xcodes } : undefined,
    );
}

/**
 * 材-02: 按 (一级, 二级) 类别前缀自动生成下一个材质编码; 未配置前缀返回空串.
 * 二级 name_en 只在父级下唯一 (如 Plastic/ABS 与 3D Printing Filament/ABS), 必须带一级.
 */
export function nextMaterialXcode(
    type1: string,
    type2: string,
): Promise<{ xcode: string; prefix: string }> {
    return request<{ xcode: string; prefix: string }>(
        `${BASE}/materials/next-xcode`,
        {
            query: { type1, type2 },
        },
    );
}

export function getMaterial(xcode: string): Promise<MaterialDetail> {
    return request<MaterialDetail>(`${BASE}/materials/detail`, {
        method: 'POST',
        dedupe: true,
        body: { xcode },
    });
}

// 写接口统一返回形态: 审批通过后生效;
// 普通用户走审批流 (message="已提交审批...", data={taskCode}), 调用点用 alertApprovalIfPending 提示.
export type WriteResult = MsgResult<{ taskCode?: string } | null>;

export function createMaterial(
    payload: CreateMaterialPayload,
): Promise<MsgResult<{ id?: number; xcode?: string; taskCode?: string }>> {
    return requestWithMsg(`${BASE}/materials`, {
        method: 'POST',
        body: payload,
    });
}

export function updateMaterial(
    xcode: string,
    payload: UpdateMaterialPayload,
): Promise<WriteResult> {
    // 业务键走 body (后端 updateReq.xcode 必填; UpdateMaterialPayload 不含 xcode)
    return requestWithMsg(`${BASE}/materials/update`, {
        method: 'POST',
        body: { xcode, ...payload },
    });
}

/** 删材料 (硬删). cascade 勾选见 fetchDeletePreview / DeleteCascadeModal. */
/** 发布 / 取消发布 (2026-09-03): 只翻 isPublished (1→2, + 发布时刻记 publishedAt), 决定 OpenAPI 是否下发; 走审批. */
export function publishMaterial(
    xcode: string,
    publish: boolean,
): Promise<WriteResult> {
    return requestWithMsg(`${BASE}/materials/publish`, {
        method: 'POST',
        body: { xcode, publish },
    });
}

export function deleteMaterial(
    xcode: string,
    payload?: DeleteCascadePayload,
): Promise<WriteResult> {
    return requestWithMsg(`${BASE}/materials/delete`, {
        method: 'POST',
        body: { xcode, ...(payload || {}) },
    });
}

/** 删材料前的关联数据预检 (弹窗勾选用). */
export function fetchMaterialDeletePreview(
    xcode: string,
): Promise<RelatedGroup[]> {
    return fetchDeletePreview(`${BASE}/materials/delete-preview`, { xcode });
}

// ---- 详情页子表 写操作 -------------------------------------------------

export interface CreateSkuPayload {
    skuCode: string;
    productName?: I18n;
    /** official / finished / '' */
    prop?: string;
    /** CMS 耗材ID */
    cmsId?: number;
    qrAttachmentId?: number;
    status?: string;
    remark?: string;
    /** 规格类型 + 规格值 (对象); 见 utils/skuSpec.validateSkuSpec */
    specGroup?: string;
    specParams?: Record<string, unknown>;
    /** 形态 (默认单品); bundle 时 items 必填且不传 xcode / 规格 */
    kind?: SkuKind;
    items?: SkuItemInput[];
}

export function createMaterialSku(
    xcode: string,
    payload: CreateSkuPayload,
): Promise<MsgResult<{ id?: number; skuCode?: string; taskCode?: string }>> {
    return requestWithMsg(`${BASE}/materials/skus/create`, {
        method: 'POST',
        body: { xcode, ...payload },
    });
}

/** SKU 库直接新增 (材质编码可空 = 暂不绑定材料). */
export function createSku(
    payload: CreateSkuPayload & { xcode?: string },
): Promise<MsgResult<{ id?: number; skuCode?: string; taskCode?: string }>> {
    return requestWithMsg(`${BASE}/skus`, { method: 'POST', body: payload });
}

export interface UpdateSkuPayload {
    productName?: I18n;
    prop?: string;
    cmsId?: number;
    status?: string;
    /** 规格: 改类型时连同值一起传 (值整组替换) */
    specGroup?: string;
    specParams?: Record<string, unknown>;
    /** 整组替换 SKU 集合的组成 (仅集合) */
    items?: SkuItemInput[];
}

/** SKU 属性「原材料」的字典 code: 该属性的 SKU 必须选规格类型并填写规格 (与后端 models.SkuPropRaw 一致). */
export const SKU_PROP_RAW = 'raw';

export function updateMaterialSku(
    xcode: string,
    skuCode: string,
    payload: UpdateSkuPayload,
): Promise<WriteResult> {
    return requestWithMsg(`${BASE}/materials/skus/update`, {
        method: 'POST',
        body: { xcode, skuCode, ...payload },
    });
}

/** 解除 SKU 与某材料的绑定 (多对多, 2026-08-24): SKU 本体与其他绑定不受影响. */
export function unbindMaterialSku(
    xcode: string,
    skuCode: string,
): Promise<WriteResult> {
    return requestWithMsg(`${BASE}/materials/skus/unbind`, {
        method: 'POST',
        body: { xcode, skuCode },
    });
}

/**
 * SKU 库全局编辑: 未绑定材料的 SKU 也能改.
 * xcodes 非 undefined = 全量替换绑定材料清单 (多对多, 2026-08-24):
 * 第一个元素为主绑定, 空数组 = 解绑全部.
 */
export function updateSkuGlobal(
    skuCode: string,
    payload: UpdateSkuPayload & { xcodes?: string[]; remark?: string },
): Promise<WriteResult> {
    return requestWithMsg(`${BASE}/skus/update`, {
        method: 'POST',
        body: { skuCode, ...payload },
    });
}

/** SKU 库全局删除. */
export function deleteSkuGlobal(skuCode: string): Promise<WriteResult> {
    return requestWithMsg(`${BASE}/skus/delete`, {
        method: 'POST',
        body: { skuCode },
    });
}

// ---- SKU 库 (以 SKU 为主键的平铺视图) -----------------------------------

export interface ListSkusParams {
    keyword?: string;
    xcode?: string;
    cmsId?: number;
    /** official / finished / none(未标) */
    prop?: string;
    /** on_shelf / off_shelf */
    status?: string;
    /** yes = 已映射 CMS 耗材ID / no = 未映射 */
    hasCms?: string;
    /** yes = 已绑定材料 / no = 未绑定 (与列表"绑定材料"列同口径) */
    hasMaterial?: string;
    /** 绑定材料一级/二级类别编码 (categoryCode) 与工艺类型 capKey: 须落在同一个绑定材料上 (2026-10-13) */
    type1?: string;
    type2?: string;
    capKey?: string;
    /** 规格类型 groupKey / none = 未选 (2026-10-14) */
    specGroup?: string;
    /** 形态 single / bundle, 空 = 全部 (SKU 库两个 tab, 2026-10-19) */
    kind?: SkuKind;
    page?: number;
    pageSize?: number;
}

export function listSkus(params: ListSkusParams): Promise<PageResult<SkuRow>> {
    return request<PageResult<SkuRow>>(`${BASE}/skus`, { query: params });
}

/** 集合编码反解出的一行; invalid 非空 = 不能作组成 (不在库 / 是集合) */
export interface ParsedSkuItem extends SkuItemInput {
    productName?: I18n;
    invalid?: string;
}

/** 按后台写法 "A-B*2" 反解 SKU 集合组成 (单品编码自身含 "-" 时按在库编码切分). */
export function parseBundleCode(skuCode: string): Promise<{ items: ParsedSkuItem[] }> {
    return request<{ items: ParsedSkuItem[] }>(`${BASE}/skus/parse-bundle-code`, { query: { skuCode } });
}

/** SearchSelect 异步下拉: 单品 SKU (SKU 集合的组成候选; 集合不能嵌套, 只搜单品). */
export async function loadSingleSkuOptions(
    query: string,
    page: number,
): Promise<{ list: { value: string; label: string }[]; hasMore: boolean }> {
    const pageSize = 50;
    const res = await listSkus({
        keyword: query || undefined,
        kind: SKU_KIND_SINGLE,
        page,
        pageSize,
    });
    return {
        list: (res.list || []).map((s) => ({
            value: s.skuCode,
            label: `${s.skuCode} · ${i18nText(s.productName || {})}`,
        })),
        hasMore: page * pageSize < res.totalCount,
    };
}

/** SKU 二维码名称来源: 主绑定材料名 / SKU 商品名 (批量导出时二选一, 不逐个手填). */
export type SkuQrNameSource = 'material' | 'product';

export interface ExportSkusPayload extends Omit<ListSkusParams, 'page' | 'pageSize' | 'cmsId'> {
    /** 非空 = 只导这些 SKU (列表勾选), 与筛选叠加 */
    skuCodes?: string[];
    format: 'xlsx' | 'csv';
    /** 带二维码贴纸时结果为 zip (表格 + qr/ 目录) */
    qr?: { size: SkuQrSize; nameSource: SkuQrNameSource };
}

/**
 * SKU 库导出 (服务端生成, 2026-10-13): xlsx / csv; 带 qr 时为 zip,
 * 贴纸按主绑定材料出码 (同单张贴纸口径), 表格末列是 qr/ 下的文件名.
 */
export function exportSkus(payload: ExportSkusPayload, filename: string): Promise<void> {
    return downloadFile(`${BASE}/skus/export`, filename, payload);
}

// ---- 理化参数 (Schema 驱动分组) -----------------------------------------

/**
 * 按组保存理化参数值. 后端契约: body.params 为 JSON 对象字符串
 * (值全为数字, 未填写的字段不带 key).
 */
export function updateMaterialPhys(
    xcode: string,
    groupKey: string,
    params: PhysGroupParams,
): Promise<WriteResult> {
    return requestWithMsg(`${BASE}/materials/phys/update`, {
        method: 'POST',
        body: { xcode, groupKey, params: JSON.stringify(params) },
    });
}

export function toggleMaterialCapability(
    xcode: string,
    capKey: string,
    isEnabled: boolean,
): Promise<WriteResult> {
    return requestWithMsg(`${BASE}/materials/capability/update`, {
        method: 'POST',
        body: { xcode, capKey, isEnabled },
    });
}

/** 整体保存推荐状态 (2026-10-28): recommended 按顺序即 sno, 两者之外的 = 未设置; 走审批. */
export function saveMaterialCapabilities(
    xcode: string,
    recommended: string[],
    notRecommended: string[],
    /** capKey → 禁用的 deviceKey; 不传 = 后端不改存量, 传了整体覆盖 */
    disabledDevices?: Record<string, string[]>,
): Promise<WriteResult> {
    return requestWithMsg(`${BASE}/materials/capability/save`, {
        method: 'POST',
        body: { xcode, recommended, notRecommended, disabledDevices },
    });
}

// ---- 批量导入 (xlsx) ------------------------------------------------------

/** 模板下载路径 (GET, 直接 <a href> 用; 拼 API_BASE 用 absoluteURL). */
export const MATERIAL_IMPORT_TEMPLATE_PATH = `${BASE}/materials/import-template`;

export interface ImportInvalidRow {
    row: number;
    reason: string;
}

export interface ImportMaterialsResult {
    total: number;
    valid: number;
    invalid: ImportInvalidRow[] | null;
    /** 走审批流的工单号 */
    taskCode?: string;
}

/** 批量导入材料 (multipart xlsx). 返回 taskCode 走审批. */
export function importMaterials(file: File): Promise<ImportMaterialsResult> {
    return uploadFile<ImportMaterialsResult>(
        `${BASE}/materials/import-xlsx`,
        file,
    );
}

/** SKU 导入模板下载路径. */
export const SKU_IMPORT_TEMPLATE_PATH = `${BASE}/skus/import-template`;

export interface ImportSkusResult extends ImportMaterialsResult {
    /** 直接入库时的新建 / 更新条数 (走审批时为空) */
    created?: number;
    updated?: number;
    /** 材质编码归一后匹配上材料的行数 / 留空未绑定的行数 */
    bound?: number;
    unbound?: number;
    /** 未绑定明细 (行照常入库, 但一个 SKU 对应多个材质 / 编码不在库, 需人工补绑) */
    unboundRows?: ImportInvalidRow[] | null;
    /** 回执 xlsx (base64): 原表 + 末列每行结果; 非全部成功时后端才生成 */
    resultFile?: string;
    resultFileName?: string;
}

/**
 * 批量导入 SKU (multipart xlsx). upsert 语义:
 * SKU 编号已存在则只覆盖表里填了值的列 (含补绑材质编码), 不存在则新建.
 */
export function importSkus(file: File): Promise<ImportSkusResult> {
    return uploadFile<ImportSkusResult>(`${BASE}/skus/import-xlsx`, file);
}

/** SKU 二维码贴纸版式 (与后端 qrsticker.Style 对齐). */
export type SkuQrSize = 'dq003' | 'comm';

export interface SkuQrSizeOption {
    key: SkuQrSize;
    label: string;
    desc: string;
    width: number;
    height: number;
}

/** 前端可选的两种贴纸尺寸 (像素为后端固定画布, 300 DPI). */
export const SKU_QR_SIZE_OPTIONS: SkuQrSizeOption[] = [
    {
        key: 'dq003',
        label: 'DQ003 专用',
        desc: '适用于 DQ003 设备识别',
        width: 2022,
        height: 3100,
    },
    {
        key: 'comm',
        label: '激光通用',
        desc: '适用于激光设备摄像头识别',
        width: 2022,
        height: 2715,
    },
];

/**
 * 生成 SKU 二维码贴纸 PNG (即时生成, 不落库, 2026-09-07).
 * 二维码内容 = https://s.xtool.com/mt/<材料旧库ID>; 正文 = "<材质编码>: <name>".
 * xcode 指定按哪条绑定材料出码 (多绑定 SKU 由用户下拉选择, 必须是该 SKU 的绑定之一); 不传 = 主绑定.
 * 返回 Blob 供预览 (object URL) 与下载 (saveBlob) 复用同一份字节.
 */
export function fetchSkuQrSticker(params: {
    skuCode: string;
    size: SkuQrSize;
    name: string;
    xcode?: string;
}): Promise<Blob> {
    const q = new URLSearchParams({
        skuCode: params.skuCode,
        size: params.size,
        name: params.name,
    });
    if (params.xcode) q.set('xcode', params.xcode);
    return fetchFileBlob(`${BASE}/skus/qr-sticker?${q.toString()}`);
}
